"use client";

import { Message } from "@/lib/core/crm/types";
import { toast } from "sonner";

export interface MessageActionOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /**
   * Suppress the success toast. Used for background actions the user did not
   * explicitly trigger (e.g. auto-mark-as-read when opening a message), where a
   * toast on every open would be noise. Errors still surface.
   */
  silent?: boolean;
}

/**
 * Direct message actions utility - bypasses complex hooks
 * Makes direct API calls to ensure mutations actually get invoked
 */
export class DirectMessageActions {
  private static async performAction(
    message: Message,
    action:
      | "delete"
      | "archive"
      | "mark_as_read"
      | "mark_as_unread"
      | "mark_important"
      | "mark_spam",
    options?: MessageActionOptions
  ) {
    try {
      const url = `/api/connections/messages/${message.provider}/${message.providerMessageId}/actions?connectionId=${message.inboxId}`;

      console.log(`🚀 DirectMessageActions: Making ${action} request to:`, url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action }),
      });

      console.log("📡 DirectMessageActions: Response status:", response.status);

      if (!response.ok) {
        // handles different response types now.
        let errorMessage = `HTTP ${response.status}`;

        // Checks to see if response is JSON
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          try {
            const errorData = await response.json();
            // now handles both 'error' and 'message' fields for backwards compatibility
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (parseError) {
            // checks to see if JSON parsing fails, uses status-based message
            if (response.status === 404) {
              errorMessage =
                "API endpoint not found. The route may not exist or the message ID may be invalid.";
            } else if (response.status === 401) {
              errorMessage = "Authentication required. Please log in again.";
            } else if (response.status === 403) {
              errorMessage = "You don't have permission to perform this action.";
            } else {
              errorMessage = `Server error (${response.status}). Please try again later.`;
            }
          }
        } else {
          // Gives a non-JSON response example: HTML 404 page, etc
          if (response.status === 404) {
            errorMessage =
              "API endpoint not found. The route may not exist or the message ID may be invalid.";
          } else if (response.status === 401) {
            errorMessage = "Authentication required. Please log in again.";
          } else if (response.status === 403) {
            errorMessage = "You don't have permission to perform this action.";
          } else {
            errorMessage = `Server error (${response.status}). Please try again later.`;
          }
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ DirectMessageActions: Success:", result);

      const actionMessages = {
        delete: "Message deleted successfully",
        archive: "Message archived successfully",
        mark_as_read: "Message marked as read",
        mark_as_unread: "Message marked as unread",
        mark_important: "Marked as important",
        mark_spam: "Marked as spam",
      };

      if (!options?.silent) {
        toast.success(actionMessages[action] || "Action completed successfully");
      }
      options?.onSuccess?.();

      return result;
    } catch (error) {
      console.error("❌ DirectMessageActions: Error:", error);
      if (!options?.silent) {
        toast.error(
          `Failed to ${action.replace("_", " ")}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
      options?.onError?.(error instanceof Error ? error : new Error("Unknown error"));
      throw error;
    }
  }

  static async deleteMessage(message: Message, options?: MessageActionOptions) {
    return this.performAction(message, "delete", options);
  }

  static async archiveMessage(message: Message, options?: MessageActionOptions) {
    return this.performAction(message, "archive", options);
  }

  static async markAsRead(message: Message, options?: MessageActionOptions) {
    return this.performAction(message, "mark_as_read", options);
  }

  static async markAsUnread(message: Message, options?: MessageActionOptions) {
    return this.performAction(message, "mark_as_unread", options);
  }

  static async markImportant(message: Message, options?: MessageActionOptions) {
    return this.performAction(message, "mark_important", options);
  }

  static async markSpam(message: Message, options?: MessageActionOptions) {
    return this.performAction(message, "mark_spam", options);
  }
}
